/**
 * UserInfoComponent
 * 
 * Displays user information for a video/content item.
 * Matches Flutter's UserInfoWidget from content_widget.dart
 * 
 * Features:
 * - User avatar (clickable)
 * - Username (clickable) - shows "You" if current user
 * - Identity badge (if applicable)
 * - Subscription status indicator
 * - More button (optional)
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { CachedImage } from '@/presentation/components/common/CachedImage';
import { UserUtils } from '@/domain/utils/userUtils';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { IdentityBadge } from '@/presentation/components/common/identity/IdentityBadge';
import { qaAttr } from '@/presentation/utils/qaAttr';

export interface UserInfoComponentProps {
  /** User ID */
  userId: string;

  /** Content/media id — used to make per-card `data-qa-id`s page-unique. */
  contentId: string;

  /** Content creation timestamp */
  contentCreatedTimestamp: number;

  /** View count */
  views?: number;

  /** Whether to show date */
  showDate?: boolean;

  /** Whether to show profile image */
  showProfileImage?: boolean;

  /** Whether to show the @username */
  showUsername?: boolean;

  /** Username to display */
  userName: string;

  /** Whether user is subscribed to this content creator */
  subscribed: boolean;

  /** Current user ID for "You" display */
  currentUserId?: string;

  /** Avatar size in pixels */
  avatarSize?: number;

  /** Avatar border radius */
  avatarRadius?: number;

  /** Avatar border width */
  avatarBorderWidth?: number;

  /** Callback when user is tapped */
  onUserTap: (userId: string) => Promise<void> | void;

  /** Whether to show the more button */
  showMore?: boolean;

  /** Callback when more button is tapped (optional) */
  onMoreTap?: (userId: string) => Promise<void> | void;

  /** Custom CSS class */
  className?: string;
}

export const UserInfoComponent: React.FC<UserInfoComponentProps> = ({
  userId,
  contentId,
  contentCreatedTimestamp: _contentCreatedTimestamp,
  views: _views,
  showDate: _showDate = false,
  showProfileImage: _showProfileImage = true,
  showUsername: _showUsername = true,
  userName,
  subscribed,
  currentUserId,
  avatarSize,
  avatarRadius,
  avatarBorderWidth,
  onUserTap,
  showMore = false,
  onMoreTap,
  className = ''
}) => {
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();

  // Show "You" if current user
  const displayName = UserUtils.getDisplayName(userId, userName, currentUserId);

  // Avatar URL
  const avatarUrl = UserUtils.generateUserProfileImageUrl(userId);

  // Responsive sizing - use platform-specific defaults if not provided
  const responsiveAvatarSize = avatarSize || responsive.avatar.small;
  const responsiveBorderWidth = avatarBorderWidth || responsive.border.thin;
  const responsiveAvatarRadius = avatarRadius ?? responsive.borderRadius.round;

  return (
    <div className={`flex items-center ${className}`}>
      {/* Avatar. Show if showProfileImage is true */}
      {_showProfileImage && (
        <button
          {...qaAttr({ id: `content-author-avatar-${userId}-${contentId}` })}
          onClick={() => onUserTap(userId)}
          className="flex-shrink-0 focus:outline-none"
          style={{
            marginRight: responsive.spacing.sm,
            width: responsiveAvatarSize,
            height: responsiveAvatarSize,
            borderRadius: responsiveAvatarRadius,
            border: `${responsiveBorderWidth}px solid ${theme.border}`,
            overflow: 'hidden',
            cursor: 'pointer'
          }}
        >
          <CachedImage
            src={avatarUrl}
            alt={`${displayName}'s avatar`}
            className="w-full h-full object-cover"
          />
        </button>
      )}

      {/* User info */}
      <div className="flex items-center flex-1 min-w-0">
        {/* Username */}
        <button
          {...qaAttr({ id: `content-author-${userId}-${contentId}` })}
          onClick={() => onUserTap(userId)}
          className="focus:outline-none hover:opacity-80 transition-opacity"
        >
          <span
            className="truncate"
            style={{ color: theme.text.primary, fontSize: responsive.font.base, fontWeight: responsive.fontWeight.semibold }}
          >
            {displayName}
          </span>
        </button>

        <span style={{ display: 'inline-flex', alignItems: 'center', marginLeft: responsive.spacing.xs }}>
          <IdentityBadge userId={userId} />
        </span>

        {/* Subscription indicator */}
        {subscribed && (
          <>
            {/* Separator dot */}
            <div
              style={{
                borderRadius: responsive.borderRadius.round,
                marginLeft: responsive.spacing.sm,
                marginRight: responsive.spacing.sm,
                width: responsive.spacing.xxs,
                height: responsive.spacing.xxs,
                backgroundColor: theme.disabled
              }}
            />

            {/* Subscribed text */}
            <span
              style={{ color: theme.text.secondary, fontSize: responsive.font.xs }}
            >
              {l10n.common.subscribed}
            </span>
          </>
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* More button */}
        {showMore && onMoreTap && (
          <button
            {...qaAttr({ id: `content-author-more-${userId}-${contentId}` })}
            onClick={() => onMoreTap(userId)}
            className="hover:opacity-80 transition-opacity focus:outline-none"
            style={{ marginLeft: responsive.spacing.lg }}
            aria-label="More options"
          >
            <AssetIcon path="/assets/vectors/more.svg" />
          </button>
        )}
      </div>
    </div>
  );
};
